import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from '../../lib/axios'
import { ArrowLeft, ArrowRight, Mail, CheckCircle, AlertCircle } from 'lucide-react'
import '../Pages.css'

export default function GoogleConnector() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    appPassword: ''
  })

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email)

  const canConnect =
    formData.firstName.trim().length > 0 &&
    formData.lastName.trim().length > 0 &&
    isValidEmail(formData.email) &&
    formData.appPassword.trim().length === 16

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleConnect = async () => {
    if (!canConnect) return

    setLoading(true)
    setError('')

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        google_app_password: formData.appPassword,
        provider: 'google'
      }

      const res = await axios.post('email/connect-google', payload)

      if (res.status === 200 || res.data?.success) {
        navigate('/email-accounts')
      } else {
        setError(res.data?.message || 'Could not connect Google account')
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Connection failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Connect Google Account</h2>
          <p>Gmail / Google Workspace</p>
        </div>
        <Link to="/email-accounts/add" className="btn btn-ghost">
          <ArrowLeft size={18} />
          <span>Back</span>
        </Link>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: step >= 1 ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
            color: step >= 1 ? 'white' : 'var(--color-text-muted)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            {step > 1 ? <CheckCircle size={18} /> : '1'}
          </div>
          <div style={{
            flex: 1,
            height: '2px',
            background: step >= 2 ? 'var(--color-primary)' : 'var(--color-border)'
          }} />
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: step >= 2 ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
            color: step >= 2 ? 'white' : 'var(--color-text-muted)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            2
          </div>
        </div>

        {/* Step 1: Instructions */}
        {step === 1 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <Mail size={20} />
                Setup App Password
              </h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>
                To connect your Gmail account, you need to create an app password. Follow these steps:
              </p>

              <ol style={{
                listStyle: 'decimal',
                paddingLeft: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <li style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  Go to your Google Account's{' '}
                  <a
                    href="https://myaccount.google.com/intro/security"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                  >
                    Security settings
                  </a>
                </li>
                <li style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  Enable{' '}
                  <a
                    href="https://myaccount.google.com/signinoptions/two-step-verification"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                  >
                    2-step verification
                  </a>
                </li>
                <li style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  Create an{' '}
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                  >
                    App password
                  </a>
                  {' '}(choose "Other" for both App and Device)
                </li>
                <li style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  Copy the 16-character app password (you'll need it in the next step)
                </li>
              </ol>

              <button
                className="btn btn-primary"
                onClick={() => setStep(2)}
                style={{ width: '100%' }}
              >
                <span>Continue</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <Mail size={20} />
                Enter Account Details
              </h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {error && (
                <div style={{
                  padding: '1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start'
                }}>
                  <AlertCircle size={18} style={{ color: '#ef4444', marginTop: '0.125rem', flexShrink: 0 }} />
                  <span style={{ color: '#dc2626', fontSize: '0.9rem' }}>{error}</span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your-email@gmail.com"
                  />
                </div>

                <div className="form-group">
                  <label>App Password *</label>
                  <input
                    type="text"
                    name="appPassword"
                    value={formData.appPassword}
                    onChange={handleChange}
                    placeholder="16-character app password"
                    maxLength={16}
                  />
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', display: 'block' }}>
                    Enter the 16-character app password from Google
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => setStep(1)}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  <ArrowLeft size={18} />
                  <span>Back</span>
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConnect}
                  disabled={!canConnect || loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Connecting...' : 'Connect Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
